'use client';

import { Product } from '@features/product/typings';
import { MainProduct } from '@features/second-chance/typings';
import { createContext, ReactNode, useContext, useRef, useState } from 'react';

import type {
  Configurator,
  ConfiguratorActions,
  ConfiguratorContextValue,
  ConfiguratorProduct,
  ConfiguratorSlotValue,
  ConfiguratorValues
} from './types';

const ConfiguratorContext = createContext<ConfiguratorContextValue | null>(null);

const mapConfiguratorValues = (configurator: Configurator): ConfiguratorValues => {
  const configuratorValues = new Map<number, ConfiguratorSlotValue[]>();

  configurator.useCases.forEach((useCase, index) => {
    const slotValue = useCase.slots.map(slot => ({
      selected: true,
      quantity: slot.quantitySelector.defaultQuantity
    }));
    configuratorValues.set(index, slotValue);
  });

  return configuratorValues;
};

const mapConfiguratorValuesToCheckboxValues = (
  configuratorValues: ConfiguratorValues,
  useCaseIndex: number
) => {
  return (configuratorValues.get(useCaseIndex) || [])
    .filter(value => value.selected)
    .map((_, index) => String(index));
};

export const ConfiguratorProvider = ({
  children,
  configurator,
  mainProduct
}: {
  children: ReactNode;
  configurator: Configurator;
  mainProduct: Product | MainProduct;
}) => {
  const [useCaseIndex, setUseCaseIndex] = useState<number>(0);
  const [selectedProductSlot, setSelectedProductSlot] = useState<number | undefined>();
  const [configuratorValues, setConfiguratorValues] = useState<ConfiguratorValues>(
    mapConfiguratorValues(configurator)
  );
  const [checkboxValues, setCheckboxValues] = useState<string[]>(
    mapConfiguratorValuesToCheckboxValues(configuratorValues, useCaseIndex)
  );

  const loadedAlternativesForUseCase = useRef<{ [useCaseIndex: number]: boolean }>({});

  const [productAlternatives, setProductAlternatives] = useState<Map<number, ConfiguratorProduct>>(
    new Map()
  );

  const [selectedAlternatives, setSelectedAlternatives] = useState<{
    [useCaseIndex: number]: { [slotIndex: number]: ConfiguratorProduct['id'] };
  }>({});

  function getProductByUseCaseAndSlot({
    useCaseIndex,
    slotIndex
  }: {
    useCaseIndex: number;
    slotIndex: number;
  }) {
    const alternativeId = selectedAlternatives[useCaseIndex]?.[slotIndex];

    if (alternativeId) {
      return getProductAlternativeById(alternativeId);
    }

    return configurator.useCases[useCaseIndex]?.slots?.[slotIndex]?.defaultProduct ?? null;
  }

  function getProductAlternativeById(productId: number) {
    return productAlternatives.get(productId) ?? null;
  }

  function getSelectedAlternativeByUseCaseAndSlot({
    useCaseIndex,
    slotIndex
  }: {
    useCaseIndex: number;
    slotIndex: number;
  }) {
    const selectedAlternativeId = selectedAlternatives[useCaseIndex]?.[slotIndex];

    if (selectedAlternativeId) {
      return getProductAlternativeById(selectedAlternativeId);
    }

    return configurator.useCases[useCaseIndex]?.slots?.[slotIndex]?.defaultProduct;
  }

  const updateConfiguratorValue: ConfiguratorActions['updateConfiguratorValue'] = productSlots => {
    const newValue = new Map(configuratorValues);

    newValue.set(useCaseIndex, productSlots);
    setConfiguratorValues(newValue);

    const newCheckboxValues = (newValue.get(useCaseIndex) || [])
      .map((value, index) => (value.selected ? String(index) : ''))
      .filter(value => !!value);
    setCheckboxValues(newCheckboxValues);
  };

  function areAlternativesLoadedForUseCase(useCaseIndex: number) {
    return loadedAlternativesForUseCase.current[useCaseIndex] !== undefined;
  }

  const updateConfiguratorDetails: ConfiguratorActions['updateConfiguratorDetails'] = slots => {
    if (!slots || slots.length === 0) return;

    loadedAlternativesForUseCase.current[useCaseIndex] = true;

    const newProductAlternatives = new Map<number, ConfiguratorProduct>(productAlternatives);

    slots.forEach(slot =>
      slot?.products?.forEach(product => {
        if (product?.id) {
          newProductAlternatives.set(product.id, product);
        }
      })
    );

    setProductAlternatives(newProductAlternatives);
  };

  const setSelectedUseCaseIndex = (index: number) => {
    const newCheckboxValues = (configuratorValues.get(index) || [])
      .map((value, index) => (value.selected ? String(index) : ''))
      .filter(value => !!value);

    setUseCaseIndex(index);
    setCheckboxValues(newCheckboxValues);
  };

  function selectAlternative({
    useCaseIndex,
    slotIndex,
    productId
  }: {
    useCaseIndex: number;
    slotIndex: number;
    productId: ConfiguratorProduct['id'];
  }) {
    setSelectedAlternatives(prev => ({
      ...prev,
      [useCaseIndex]: { ...prev[useCaseIndex], [slotIndex]: productId }
    }));
  }

  const states = {
    selectedUseCaseIndex: useCaseIndex,
    selectedProductSlot,
    checkboxValues,
    configuratorValues
  };

  const actions = {
    updateConfiguratorValue,
    setSelectedUseCaseIndex,
    setSelectedProductSlot,
    setCheckboxValues,
    updateConfiguratorDetails,
    getProductByUseCaseAndSlot,
    areAlternativesLoadedForUseCase,
    getProductAlternativeById,
    selectAlternative,
    getSelectedAlternativeByUseCaseAndSlot
  };

  return (
    <ConfiguratorContext.Provider value={{ actions, configurator, states, mainProduct }}>
      {children}
    </ConfiguratorContext.Provider>
  );
};

export const useConfigurator = () => {
  const context = useContext(ConfiguratorContext);

  if (!context) {
    throw new Error('useConfigurator must be used within a ConfiguratorProvider');
  }

  return context;
};
